import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { ordersAPI, listingsAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Package,
  ShoppingBag,
  Clock,
  CheckCircle,
  ArrowRight,
  Plus,
  TrendingUp,
  DollarSign,
  AlertCircle
} from 'lucide-react';

const SellerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, listingsRes] = await Promise.all([
        ordersAPI.getSellerOrders(),
        listingsAPI.getMy()
      ]);
      setOrders(ordersRes.data);
      setListings(listingsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-800',
      approved: 'bg-sky-100 text-sky-800',
      in_fulfillment: 'bg-blue-100 text-blue-800',
      delivered: 'bg-emerald-100 text-emerald-800',
      completed: 'bg-green-100 text-green-800',
      disputed: 'bg-red-100 text-red-800',
      cancelled: 'bg-slate-100 text-slate-800',
      draft: 'bg-slate-100 text-slate-600',
      pending_approval: 'bg-amber-100 text-amber-800',
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  const stats = {
    total_listings: listings.length,
    active_listings: listings.filter(l => l.status === 'approved').length,
    total_orders: orders.length,
    pending_orders: orders.filter(o => ['pending', 'approved', 'in_fulfillment'].includes(o.status)).length,
    total_revenue: orders.filter(o => ['delivered', 'completed'].includes(o.status)).reduce((acc, o) => acc + o.total_price, 0),
  };

  if (loading) {
    return (
      <Layout showFooter={false}>
        <div className="container mx-auto px-4 lg:px-8 py-8">
          <Skeleton className="h-8 w-48 mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showFooter={false}>
      <div className="container mx-auto px-4 lg:px-8 py-8" data-testid="seller-dashboard">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 mb-1">
              Seller Dashboard
            </h1>
            <p className="text-slate-600">Manage your listings and orders</p>
          </div>
          <Button onClick={() => navigate('/seller/listings/new')} data-testid="create-listing-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Listing
          </Button>
        </div>

        {/* Verification Notice */}
        {!user?.is_verified && (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <div>
                  <div className="font-medium text-amber-900">Verification Pending</div>
                  <div className="text-sm text-amber-700">Your account is awaiting admin verification. Some features may be limited.</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-sky-600" />
              </div>
              <div>
                <div className="stat-card-value">{stats.active_listings}</div>
                <div className="stat-card-label">Active Listings</div>
              </div>
            </div>
          </Card>
          <Card className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <div className="stat-card-value">{stats.pending_orders}</div>
                <div className="stat-card-label">Pending Orders</div>
              </div>
            </div>
          </Card>
          <Card className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <div className="stat-card-value">{stats.total_orders}</div>
                <div className="stat-card-label">Total Orders</div>
              </div>
            </div>
          </Card>
          <Card className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="stat-card-value">${stats.total_revenue.toLocaleString()}</div>
                <div className="stat-card-label">Total Revenue</div>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Orders */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Recent Orders</CardTitle>
              <Link to="/seller/orders" className="text-sm text-sky-600 hover:text-sky-700">
                View all
              </Link>
            </CardHeader>
            <CardContent>
              {orders.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No orders yet
                </div>
              ) : (
                <div className="space-y-3">
                  {orders.slice(0, 5).map((order) => (
                    <div
                      key={order.id}
                      className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/seller/orders/${order.id}`)}
                      data-testid={`seller-order-${order.id}`}
                    >
                      <div>
                        <div className="font-medium text-sm text-slate-900">{order.campaign_name}</div>
                        <div className="text-xs text-slate-500">{order.buyer_name}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={getStatusColor(order.status)}>
                          {order.status.replace('_', ' ')}
                        </Badge>
                        <span className="font-semibold text-sm">${order.total_price}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* My Listings */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>My Listings</CardTitle>
              <Link to="/seller/listings" className="text-sm text-sky-600 hover:text-sky-700">
                View all
              </Link>
            </CardHeader>
            <CardContent>
              {listings.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">No listings yet</p>
                  <Button size="sm" onClick={() => navigate('/seller/listings/new')}>
                    Create your first listing
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {listings.slice(0, 5).map((listing) => (
                    <div
                      key={listing.id}
                      className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/seller/listings/${listing.id}`)}
                    >
                      <div>
                        <div className="font-medium text-sm text-slate-900 line-clamp-1">{listing.title}</div>
                        <div className="text-xs text-slate-500">{listing.category}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={getStatusColor(listing.status)}>
                          {listing.status.replace('_', ' ')}
                        </Badge>
                        <span className="text-xs text-slate-500">{listing.total_orders} orders</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default SellerDashboard;
